import constants from "./constants";
import { convertDateTimeToTimeString, isTimeValid, getDateWithUtcHoursAndMinFromTimeString } from "@dataGrid/gridUtilities";
import { getTerminalDateWithOffset } from "../../utils/utils";
import { findMatchingStraightTractor } from "./utils";
import { isValid } from "date-fns";

/** Case-insensitive string match. */
const matchIgnoreCase = (a, b) => (a ?? "")?.toLowerCase() === (b ?? "")?.toLowerCase();

/** Find first item matching predicate; returns null if list empty or no match. */
const findInList = (list, predicate) =>
    list?.length && predicate ? list.find(predicate) ?? null : null;

const findTrailerById = (trailerList, trailerIdOrObject) => {
    const trailerId =
        typeof trailerIdOrObject === "string" ? trailerIdOrObject : trailerIdOrObject?.trailerId;
    if (!trailerId) return null;
    return findInList(trailerList, (equipment) => matchIgnoreCase(equipment.trailerId, trailerId));
};

const findTractorByLabel = (tractorList, label) =>
    findInList(tractorList, (tractor) => matchIgnoreCase(tractor.label, label));

const findDollyByLabel = (dollyList, label) =>
    findInList(dollyList, (dolly) => matchIgnoreCase(dolly.label, label));

const findDriverById = (driverList, driverId) =>
    findInList(driverList, (driver) => driver.driverId === driverId);

/** Match current trailer row to a pre-dispatch selection entry (by id, else trailer #). */
const trailerMatchesSelectedSlot = (trailer, selectedTrailer) => {
    if (!trailer || !selectedTrailer) return false;
    const tid = trailer.trailerId ?? trailer.id;
    const sid = selectedTrailer.trailerId ?? selectedTrailer.id;
    if (tid && sid) return matchIgnoreCase(tid, sid);
    return matchIgnoreCase(selectedTrailer.trailerNumber, trailer.trailerNumber);
};


const isTrailerPreSelected = (trailer, selectedTrailer, index) => {
    if (!selectedTrailer?.length || !trailer) return false;
    if (index) {
        return trailerMatchesSelectedSlot(trailer, selectedTrailer[index]);
    }
    return selectedTrailer.some((st) => trailerMatchesSelectedSlot(trailer, st));
};

const isDollyPreSelected = (trailer, dolly, selectedTrailer) =>
    Boolean(dolly && isTrailerPreSelected(trailer, selectedTrailer) &&
        selectedTrailer.some((st) => matchIgnoreCase(st.dollyNumber, dolly.label)));

const getTrailersDetailsFromSelectedTrailer = (selectedTrailer, dollyList) =>
    selectedTrailer.map((trailer) => {
        const dollyLabel = trailer.dollyNumber || constants.DEFAULT_DOLLY;
        const dolly = dollyList?.length
            ? (findDollyByLabel(dollyList, dollyLabel) ?? null)
            : dollyLabel;
        const item = {
            ...trailer,
            id: trailer.trailerId,
            trailerNumber: trailer.trailerNumber || null,
            manifestNumber: trailer.manifestNumber || null,
            routeName: trailer.routeName || null,
            idealStartTime: trailer.plannedRouteStartTime || trailer.idealStartTime || null,
            isManualStartTime: !(trailer.plannedRouteStartTime || trailer.idealStartTime ) || false,
            dolly,
        };
        if (trailer.isAlreadyDispatchedRoute !== undefined) {
            item.isAlreadyDispatchedRoute = trailer.isAlreadyDispatchedRoute;
        }
        return item;
    });

const shouldDisableRouteNameEdit = (trailer, selectedTrailer, index) =>
    Boolean(selectedTrailer?.length && isTrailerPreSelected(trailer, selectedTrailer, index));

const getInitialTrailerDetails = (props) => {
    const { selectedTrailer, dollyList } = props || {};
    return selectedTrailer?.length
        ? getTrailersDetailsFromSelectedTrailer(selectedTrailer, dollyList)
        : null;
};

const trailerIdealStartDefault = (t) => t?.idealStartTime ?? t?.plannedRouteStartTime ?? null;

const trailerIdealStartKey = (t) => t?.trailerId ?? t?.id ?? null;

/** Stable row signature for ideal-start alignment (length + key per row). */
const getTrailerRowsIdealStartSignature = (trailerDetails) =>
  (trailerDetails || []).map((t) => trailerIdealStartKey(t)).join("|");

const buildIdealStartTimesByTrailer = ({ preDispatchIdealStartTimesByTrailer, trailerDetails }) => {
    const next = (trailerDetails || []).reduce((acc, t) => {
        const key = trailerIdealStartKey(t);
        if (!key || key in acc) return acc;
        acc[key] = trailerIdealStartDefault(t);
        return acc;
    }, { ...(preDispatchIdealStartTimesByTrailer || {}) });
    return next;
};

const getIdealStartTimesForTrailers = ({ preDispatchIdealStartTimesByTrailer, trailerDetails }) => {
    const map = preDispatchIdealStartTimesByTrailer || {};
    return (trailerDetails || []).map((t) => {
        const key = trailerIdealStartKey(t);
        return key && key in map ? map[key] : trailerIdealStartDefault(t);
    });
};

const updateIdealStartTimesByTrailerAtIndex = ({
    preDispatchIdealStartTimesByTrailer,
    trailerDetails,
    index,
    value,
}) => {
    const key = trailerIdealStartKey(trailerDetails?.[index]);
    if (!key) return preDispatchIdealStartTimesByTrailer || {};
    const next = { ...(preDispatchIdealStartTimesByTrailer || {}) };
    if (!value) {
        next[key] = null;
        return next;
    }
    const timeString = convertDateTimeToTimeString(value);
    if (isTimeValid(timeString)) next[key] = value;
    return next;
};

/** When a row key changes (e.g. client id → trailerId), move the stored ideal start time. */
const migratePreDispatchIdealStartTimesMap = ({
    preDispatchIdealStartTimesByTrailer,
    previousTrailerDetails,
    nextTrailerDetails,
}) => {
    const prev = previousTrailerDetails || [];
    const next = nextTrailerDetails || [];
    const len = Math.max(prev.length, next.length);
    return [...Array(len).keys()].reduce((acc, i) => {
        const prevKey = trailerIdealStartKey(prev[i]);
        const nextKey = trailerIdealStartKey(next[i]);
        if (
            prevKey &&
            nextKey &&
            prevKey !== nextKey &&
            acc[prevKey] != null &&
            (acc[nextKey] === undefined || acc[nextKey] === null)
        ) {
            const { [prevKey]: movedValue, ...rest } = acc;
            return { ...rest, [nextKey]: movedValue };
        }
        return acc;
    }, { ...(preDispatchIdealStartTimesByTrailer || {}) });
};

/* helper: migrate ideal-start map when trailer rows change, and detect same row identity. */
const resolvePreDispatchIdealStartSync = (prevState, trailerDetails, propsSelectedTrailer) => {
    const previousTrailers =
        prevState.preDispatchTrailers ?? propsSelectedTrailer ?? [];
    const sameTrailerRows =
        getTrailerRowsIdealStartSignature(previousTrailers) ===
        getTrailerRowsIdealStartSignature(trailerDetails);
    const migrateFrom =
        prevState.preDispatchTrailerDetailsForIdealStart ?? previousTrailers;
    const migrated = migratePreDispatchIdealStartTimesMap({
        preDispatchIdealStartTimesByTrailer: prevState.preDispatchIdealStartTimesByTrailer,
        previousTrailerDetails: migrateFrom,
        nextTrailerDetails: trailerDetails,
    });
    const preDispatchIdealStartTimesByTrailer = buildIdealStartTimesByTrailer({
        preDispatchIdealStartTimesByTrailer: migrated,
        trailerDetails,
    });
    return {
        sameTrailerRows,
        preDispatchIdealStartTimesByTrailer,
    };
};

/**
 * Pre-dispatch only: merge straight-truck equipment into one trailer slot.
 * Default dolly applies only to the replaced row; other rows unchanged.
 */
const applyStraightTruckTrailerAtIndexForPreDispatch = (
    trailerDetails,
    updateIndex,
    trailerData,
    defaultDolly
) =>
    trailerDetails.map((trailer, index) =>
        index === updateIndex ? { ...trailerData, dolly: defaultDolly ?? null } : trailer
    );

/** Pre-dispatch: first trailer to use when list is empty and a selection exists. */
const getFirstTrailerFromSelection = (selectedTrailer, dollyList) => {
    if (!selectedTrailer?.length) return null;
    const details = getTrailersDetailsFromSelectedTrailer(selectedTrailer, dollyList);
    return details?.length ? details[0] : null;
};

// Pick tractor from label, then /TBF when allowed (pre-dispatch). Normal dispatch skips /TBF default.
const resolveSuggestedTractorFromList = ({ tractorList, selectedTractorLabel, useDefaultTractor }) => {
    const tractorMatchingSelection = selectedTractorLabel
        ? findTractorByLabel(tractorList, selectedTractorLabel)
        : null;
    if (tractorMatchingSelection) {
        return tractorMatchingSelection;
    }
    if (!useDefaultTractor) {
        return null;
    }
    return findTractorByLabel(tractorList, constants.DEFAULT_TRACTOR) ?? null;
};

const getSuggestedTractorOnLoad = ({ tractorList, selectedTractor, initialTrailerDetails, useDefaultTractor = true }) => {
    if (!tractorList?.length) return null;
    if (initialTrailerDetails?.length > 0 && initialTrailerDetails[0]?.isStraightTruck) {
        // if selected trailer is straight truck, return matching tractor
        const matchingTractor = findMatchingStraightTractor(initialTrailerDetails[0], tractorList);
        if (matchingTractor) {
            return matchingTractor;
        }
    }
    return resolveSuggestedTractorFromList({
        tractorList,
        selectedTractorLabel: selectedTractor,
        useDefaultTractor,
    });
};

/** Get dolly label from string or object. */
const getDollyLabel = (dolly) =>
    dolly && typeof dolly === "string" ? dolly : dolly?.label ?? null;

const getSuggestedDollyOnLoad = (props, trailerDetails, canShowDefaultDolly) => {
    const { dollyList, selectedTrailer, isPreDispatch } = props || {};
    const showDefaultDolly = canShowDefaultDolly ?? isPreDispatch;
    if (!dollyList?.length || !Array.isArray(trailerDetails)) return null;
    return trailerDetails.map((trailer, index) => {
        if (index === 0) return trailer;
        if (trailer.dolly?.label != null) return trailer;
        if (trailer.dolly) {
            const label = getDollyLabel(trailer.dolly);
            if (label) {
                const resolved = findDollyByLabel(dollyList, label);
                if (resolved) return { ...trailer, dolly: resolved };
            }
        }
        const entry = trailerMatchesSelectedSlot(selectedTrailer, trailer.trailerNumber);
        if (entry?.dollyNumber) {
            const matched = findDollyByLabel(dollyList, entry.dollyNumber);
            if (matched) return { ...trailer, dolly: matched };
        }
        if (showDefaultDolly) {
            const dolly = findDollyByLabel(dollyList, constants.DEFAULT_DOLLY) ?? dollyList[0];
            return { ...trailer, dolly };
        }
        return trailer;
    });
};

const computeUpdateState = (state, props, canShowDefaultDolly = true) => {
    const { allTrailers, dollyList, tractorList, selectedTrailer } = props;
    const defaultDolly = canShowDefaultDolly
        ? findDollyByLabel(dollyList, constants.DEFAULT_DOLLY) ?? null
        : null;
    const newTrailerDetails = state.trailerDetails.map((eachTrailer, index) => {
        const trailerIdOrId = eachTrailer?.trailerId ?? eachTrailer?.id;
        let trailer = findTrailerById(allTrailers, trailerIdOrId);
        if (!trailer && index === 0 && selectedTrailer) {
            trailer = findTrailerById(allTrailers, selectedTrailer);
        }
        if (!trailer) {
            return {
                id: eachTrailer?.id,
                trailerNumber: null,
                manifestNumber: null,
                routeName: null,
                dolly: eachTrailer?.dolly ?? defaultDolly ?? null,
            };
        }
        const dollyLabel = getDollyLabel(eachTrailer.dolly);
        const dolly = dollyLabel ? findDollyByLabel(dollyList, dollyLabel) : null;
        return { ...trailer, dolly: dolly ?? defaultDolly ?? null };
    });
    const newTractor =
        state.tractor?.label != null
            ? findTractorByLabel(tractorList, state.tractor.label)
            : null;
    const shouldUpdateTractor = newTractor?.value !== state.tractor?.value;
    return {
        trailerDetails: newTrailerDetails,
        ...(shouldUpdateTractor && { tractor: newTractor }),
    };
};


const getTerminalDateWithOffsetFromTimeString = (timeString, terminalOffset) => {
    const stringTime = isValid(timeString) ? timeString : getDateWithUtcHoursAndMinFromTimeString(timeString) ;
    if (stringTime) {
        const date = getTerminalDateWithOffset(stringTime, terminalOffset);
        return date;
    }
    return null;
};

const normalizeDispatchPayload = (data, props) => {
    if (!data?.trailerDetails) return data;
    const { allTrailers, terminalOffset } = props;
    const hasAllTrailers = allTrailers?.length > 0;
    const trailerDetails = data.trailerDetails.map((trailer, index) => {
        let trailerId = trailer.trailerId || trailer.id;
        if (!trailerId && trailer.trailerNumber && hasAllTrailers) {
            const found = findTrailerById(allTrailers, trailer.trailerNumber);
            trailerId = found?.trailerId;
        }
        const item = {
            trailerId: trailerId || null,
            manifestNumber: trailer.manifestNumber || null,
            dollyId: index ? trailer.dollyId ?? null : null,
            routeName: trailer.routeName || null,
        };
        if (data.isPreDispatch) {
            item.isAlreadyDispatchedRoute = trailer.isAlreadyDispatchedRoute || false;
            item.routeStartTime = getTerminalDateWithOffsetFromTimeString(
                trailer.plannedRouteStartTime,
                terminalOffset
            );
            item.isManualStartTime = trailer.isManualStartTime || false;
        }
        return item;
    });
    return { ...data, trailerDetails };
};

const getPreSelectedTrailer = ({ route = null, trailer = {}, isAlreadyDispatchedRoute = false }) => {
    if (!route) {
        // If route is not provided, return the trailer with isAlreadyDispatchedRoute flag which means noraml tariler 
        return {
            ...trailer,
            isAlreadyDispatchedRoute,
        };
    }
    // If route is provided, which means pre-selected route trailer from dispatchgrid, we need to caluculate the numberOfProsLoaded from the route stops
    const numberOfProsLoaded = (route?.stops ?? []).reduce(
        (total, stop) => total + (Number(stop?.numberOfBills) || 0),
        0
    );
    return {
        ...route,
        ...trailer,
        numberOfProsLoaded,
        isAlreadyDispatchedRoute,
    };
};

// React key — remount panel when driver changes or trailer selection diverges from pre-selected rows.
const getDispatchTrailerKey = (selectedTrailers, preDispatchTrailers, driverId) => {
    const closedTrailerId = selectedTrailers?.[0]?.trailerId;
    const preSelectedTrailerId = preDispatchTrailers?.[0]?.trailerId;

    // Incomplete trailer context — tie key to driver so panel resets on driver change.
    if (!closedTrailerId || !preSelectedTrailerId) {
        return `${driverId ?? "NA"}-DriverPreDispatchTrailerPanel`;
    }

    const trailerSelectionChanged = closedTrailerId !== preSelectedTrailerId;
    return `${trailerSelectionChanged ? driverId : "NA"}-DriverPreDispatchTrailerPanel`;
};

/** Editable only when neither ideal nor planned start time is set; if either exists, lock the field. */
const isStartTimeEditable = (trailer) =>
    !(trailer?.idealStartTime || trailer?.plannedRouteStartTime);

export {
    findTrailerById,
    findTractorByLabel,
    findDollyByLabel,
    findDriverById,
    isTrailerPreSelected,
    isDollyPreSelected,
    getTrailersDetailsFromSelectedTrailer,
    getInitialTrailerDetails,
    buildIdealStartTimesByTrailer,
    getIdealStartTimesForTrailers,
    updateIdealStartTimesByTrailerAtIndex,
    migratePreDispatchIdealStartTimesMap,
    getTrailerRowsIdealStartSignature,
    resolvePreDispatchIdealStartSync,
    applyStraightTruckTrailerAtIndexForPreDispatch,
    getFirstTrailerFromSelection,
    getSuggestedTractorOnLoad,
    getSuggestedDollyOnLoad,
    computeUpdateState,
    normalizeDispatchPayload,
    shouldDisableRouteNameEdit,
    getPreSelectedTrailer,
    getDispatchTrailerKey,
    isStartTimeEditable
};